import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { createPortal } from "react-dom";
import DashboardLayout from "./DashboardLayout";
import { API_BASE } from "../utils/apiBase";

function ViewQuestionModulePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setDetail, setSetDetail] = useState(null);
  const [editQuestion, setEditQuestion] = useState(null);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [questionPageSize, setQuestionPageSize] = useState("10");
  const [questionPage, setQuestionPage] = useState(1);
  const [setPageSize, setSetPageSize] = useState("10");
  const [setPage, setSetPage] = useState(1);
  const initialSetIdRef = useRef(new URLSearchParams(window.location.search).get("set"));
  const [restoringSet, setRestoringSet] = useState(Boolean(initialSetIdRef.current));

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/question-modules`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await load();
        const setId = initialSetIdRef.current;
        if (setId) {
          await openView(setId, false);
        }
      } catch (e) {
      } finally {
        if (mounted) setRestoringSet(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeType = (type) => (type || "").toString().trim().toLowerCase().replace(/\s+/g, "_");
  const isImageType = (type) => normalizeType(type).startsWith("image_");
  const isTrueFalseType = (type) => {
    const t = normalizeType(type);
    return t === "true_false" || t === "image_true_false";
  };
  const isMultipleType = (type) => {
    const t = normalizeType(type);
    return t === "multiple_choice" || t === "image_multiple_choice";
  };
  const allSetQuestions = useMemo(() => (setDetail?.questions || []), [setDetail]);
  const listPageSizeNumber = useMemo(() => (setPageSize === "all" ? (rows.length || 1) : Number(setPageSize || 10)), [setPageSize, rows.length]);
  const listTotalPages = useMemo(() => Math.max(1, Math.ceil((rows.length || 0) / listPageSizeNumber)), [rows.length, listPageSizeNumber]);
  const paginatedSetRows = useMemo(() => {
    const start = (setPage - 1) * listPageSizeNumber;
    return rows.slice(start, start + listPageSizeNumber);
  }, [rows, setPage, listPageSizeNumber]);
  const questionPageSizeNumber = useMemo(() => (questionPageSize === "all" ? (allSetQuestions.length || 1) : Number(questionPageSize || 10)), [questionPageSize, allSetQuestions.length]);
  const questionTotalPages = useMemo(() => Math.max(1, Math.ceil((allSetQuestions.length || 0) / questionPageSizeNumber)), [allSetQuestions.length, questionPageSizeNumber]);
  const paginatedQuestions = useMemo(() => {
    const start = (questionPage - 1) * questionPageSizeNumber;
    return allSetQuestions.slice(start, start + questionPageSizeNumber);
  }, [allSetQuestions, questionPage, questionPageSizeNumber]);

  useEffect(() => {
    setQuestionPage(1);
  }, [setDetail?.module?.id, questionPageSize]);
  useEffect(() => {
    setSetPage(1);
  }, [setPageSize, rows.length]);

  useEffect(() => {
    if (questionPage > questionTotalPages) {
      setQuestionPage(questionTotalPages);
    }
  }, [questionPage, questionTotalPages]);
  useEffect(() => {
    if (setPage > listTotalPages) {
      setSetPage(listTotalPages);
    }
  }, [setPage, listTotalPages]);

  const openView = async (id, updateUrl = true) => {
    const res = await axios.get(`${API_BASE}/question-modules/${id}?_ts=${Date.now()}`);
    setSetDetail(res.data || null);
    if (updateUrl) {
      const next = `${window.location.pathname}?set=${encodeURIComponent(String(id))}`;
      window.history.replaceState({}, "", next);
    }
  };

  const refreshCurrentSet = async () => {
    if (!setDetail?.module?.id) return;
    await openView(setDetail.module.id);
  };

  const currentCorrectArray = (value) =>
    String(value || "")
      .split("|")
      .map((x) => x.trim().toUpperCase())
      .filter(Boolean);

  const toggleCorrectOption = (key) => {
    setEditQuestion((prev) => {
      const curr = currentCorrectArray(prev?.correct_answers || "A");
      const has = curr.includes(key);
      const next = has ? curr.filter((x) => x !== key) : [...curr, key];
      return { ...prev, correct_answers: (next.length ? next : ["A"]).join("|") };
    });
  };

  const saveQuestion = async () => {
    if (!editQuestion || !setDetail?.module?.id) return;
    setSavingQuestion(true);
    try {
      const payload = {
        question: editQuestion.question || "",
        question_type: normalizeType(editQuestion.question_type || "single_choice"),
        option_a: editQuestion.option_a || "",
        option_b: editQuestion.option_b || "",
        option_c: editQuestion.option_c || "",
        option_d: editQuestion.option_d || "",
        correct_answers: editQuestion.correct_answers || "A",
        marks: editQuestion.marks ?? 1,
      };
      const formData = new FormData();
      Object.entries(payload).forEach(([k, v]) => formData.append(k, v ?? ""));
      if (editQuestion.new_image_file) {
        formData.append("question_image", editQuestion.new_image_file);
      }
      await axios.post(`${API_BASE}/question-modules/${setDetail.module.id}/questions/${editQuestion.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire("Saved", "Question updated.", "success");
      setEditQuestion(null);
      await openView(setDetail.module.id);
    } catch (err) {
      Swal.fire("Failed", err?.response?.data?.message || "Update failed", "error");
    } finally {
      setSavingQuestion(false);
    }
  };

  const formatCorrectForList = (q) => {
    if (q.correct_answers_display) return q.correct_answers_display;
    if (Array.isArray(q.correct_answers_list) && q.correct_answers_list.length) return q.correct_answers_list.join("|");
    try {
      const arr = JSON.parse(q.correct_answers_json || "[]");
      if (Array.isArray(arr) && arr.length) return arr.join("|");
    } catch (e) {}
    return q.correct_option || "-";
  };

  const renderCorrectBadges = (q) => {
    const raw = formatCorrectForList(q);
    if (!raw || raw === "-") return "-";
    const parts = String(raw)
      .split("|")
      .map((x) => x.trim())
      .filter(Boolean);
    const colors = {
      A: { bg: "#dbeafe", text: "#1d4ed8" },
      B: { bg: "#dcfce7", text: "#166534" },
      C: { bg: "#fef3c7", text: "#92400e" },
      D: { bg: "#fee2e2", text: "#991b1b" },
      TRUE: { bg: "#dcfce7", text: "#166534" },
      FALSE: { bg: "#fee2e2", text: "#991b1b" },
    };
    return (
      <div className="d-flex gap-1 flex-wrap justify-content-center align-items-center">
        {parts.map((p, idx) => {
          const key = p.toUpperCase();
          const c = colors[key] || { bg: "#e5e7eb", text: "#374151" };
          return (
            <span key={`${p}-${idx}`} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 28, padding: "2px 8px", borderRadius: 999, fontWeight: 700, fontSize: 12, background: c.bg, color: c.text, border: "1px solid rgba(0,0,0,.06)" }}>
              {p}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {restoringSet ? (
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">Loading set view...</div>
          </div>
        ) : null}

        {!restoringSet && !setDetail ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h3 className="mb-2">View Question Module</h3>
              <div className="table-responsive">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <div className="small text-muted">
                    Showing {rows.length === 0 ? 0 : ((setPage - 1) * listPageSizeNumber) + 1} to {Math.min(setPage * listPageSizeNumber, rows.length)} of {rows.length}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <select className="form-select form-select-sm" style={{ width: 110 }} value={setPageSize} onChange={(e) => setSetPageSize(e.target.value)}>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="all">All</option>
                    </select>
                    <button className="btn btn-sm btn-outline-secondary" disabled={setPage <= 1} onClick={() => setSetPage((p) => Math.max(1, p - 1))}>Prev</button>
                    <span className="small">Page {setPage} / {listTotalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={setPage >= listTotalPages} onClick={() => setSetPage((p) => Math.min(listTotalPages, p + 1))}>Next</button>
                  </div>
                </div>
                <table className="table table-bordered align-middle">
                  <thead>
                    <tr>
                      <th>Sl No</th>
                      <th>Set Name</th>
                      <th>Source</th>
                      <th>Academy</th>
                      <th>Course</th>
                      <th>Questions</th>
                      <th>Total Marks</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="8">Loading...</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan="8">No question module found.</td></tr>
                    ) : paginatedSetRows.map((r, idx) => (
                      <tr key={r.id}>
                        <td>{((setPage - 1) * listPageSizeNumber) + idx + 1}</td>
                        <td>{r.set_name}</td>
                        <td>{r.source_type || "manual"}</td>
                        <td>{r.academy_category || "-"}</td>
                        <td>{r.course_name || "-"}</td>
                        <td>{r.question_count || 0}</td>
                        <td>{Number(r.total_marks || 0)}</td>
                        <td><button className="btn btn-sm btn-info text-white" onClick={() => openView(r.id)}>View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : !restoringSet && setDetail ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Set: {setDetail?.module?.set_name} <span className="badge bg-primary ms-2">Total Marks: {Number(setDetail?.total_marks || 0)}</span></h5>
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-primary btn-sm" onClick={refreshCurrentSet}>Refresh</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setSetDetail(null); window.history.replaceState({}, "", window.location.pathname); }}>Back</button>
                </div>
              </div>
              <div className="table-responsive">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <div className="small text-muted">
                    Showing {allSetQuestions.length === 0 ? 0 : ((questionPage - 1) * questionPageSizeNumber) + 1}
                    {" "}to{" "}
                    {Math.min(questionPage * questionPageSizeNumber, allSetQuestions.length)} of {allSetQuestions.length}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 110 }}
                      value={questionPageSize}
                      onChange={(e) => setQuestionPageSize(e.target.value)}
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="all">All</option>
                    </select>
                    <button className="btn btn-sm btn-outline-secondary" disabled={questionPage <= 1} onClick={() => setQuestionPage((p) => Math.max(1, p - 1))}>Prev</button>
                    <span className="small">Page {questionPage} / {questionTotalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={questionPage >= questionTotalPages} onClick={() => setQuestionPage((p) => Math.min(questionTotalPages, p + 1))}>Next</button>
                  </div>
                </div>
                <table className="table table-bordered align-middle">
                  <thead><tr><th>#</th><th>Type</th><th>Question</th><th>Image</th><th>Options</th><th>Correct</th><th>Marks</th><th className="text-center">Action</th></tr></thead>
                  <tbody>
                    {paginatedQuestions.map((q, i) => (
                      <tr key={q.id || i}>
                        <td>{((questionPage - 1) * questionPageSizeNumber) + i + 1}</td>
                        <td>{q.question_type}</td>
                        <td>{q.question}</td>
                        <td>
                          {q.question_image ? (
                            <img src={`${API_BASE.replace(/\/api$/, "")}/uploads/question-modules/${q.question_image}`} alt="Question" style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6, border: "1px solid #e2e8f0" }} />
                          ) : "-"}
                        </td>
                        <td style={{ minWidth: 220 }}>
                          <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 8px", background: "#f8fafc" }}>
                            {[q.option_a, q.option_b, q.option_c, q.option_d].filter(Boolean).map((opt, idx) => (
                              <div key={idx}><b>{String.fromCharCode(65 + idx)}.</b> {opt}</div>
                            ))}
                          </div>
                        </td>
                        <td className="text-center align-middle">{renderCorrectBadges(q)}</td>
                        <td className="text-center align-middle">{Number(q.marks ?? 1)}</td>
                        <td className="text-center align-middle">
                          <div className="d-flex justify-content-center align-items-center">
                            <button className="btn btn-sm btn-warning" title="Edit" aria-label="Edit question" onClick={() => setEditQuestion({
                              ...q,
                              question_type: normalizeType(q.question_type),
                              correct_answers: q.correct_answers_json ? (JSON.parse(q.correct_answers_json || "[]").join("|") || q.correct_option || "A") : (q.correct_option || "A"),
                              existing_image_url: q.question_image ? `${API_BASE.replace(/\/api$/, "")}/uploads/question-modules/${q.question_image}` : "",
                              new_image_file: null,
                              new_image_preview: "",
                              marks: Number(q.marks ?? 1),
                            })}>
                              <i className="bi bi-pencil-square"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {editQuestion ? createPortal(
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }} onClick={() => setEditQuestion(null)}>
          <div style={{ width: "min(980px, 100%)", maxHeight: "90vh", background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 18px 45px rgba(0,0,0,.25)" }} onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
              <h5 className="mb-0">Edit Question</h5>
              <button className="btn-close" onClick={() => setEditQuestion(null)} />
            </div>
            <div className="p-3" style={{ overflowY: "auto", maxHeight: "calc(90vh - 116px)" }}>
              <div className="row g-2">
                <div className="col-md-6"><label className="form-label">Type</label><select className="form-select" value={normalizeType(editQuestion.question_type) || "single_choice"} onChange={(e) => setEditQuestion((p) => ({ ...p, question_type: normalizeType(e.target.value) }))}><option value="multiple_choice">multiple_choice</option><option value="single_choice">single_choice</option><option value="true_false">true_false</option><option value="image_multiple_choice">image_multiple_choice</option><option value="image_single_choice">image_single_choice</option><option value="image_true_false">image_true_false</option></select></div>
                <div className="col-md-12"><label className="form-label">Question</label><textarea className="form-control" rows="3" value={editQuestion.question || ""} onChange={(e) => setEditQuestion((p) => ({ ...p, question: e.target.value }))} /></div>
                <div className="col-md-12">
                  <label className="form-label">Question Image (.jpg/.jpeg)</label>
                  {!isImageType(editQuestion.question_type) ? <div className="small text-muted mb-2">Image upload সক্রিয় করতে Type থেকে `image_*` option select করুন।</div> : null}
                  <input type="file" accept=".jpg,.jpeg,image/jpeg" className="form-control" onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setEditQuestion((p) => ({ ...p, new_image_file: f, new_image_preview: f ? URL.createObjectURL(f) : "" }));
                  }} />
                  <div className="d-flex gap-3 mt-2 align-items-start flex-wrap">
                    {editQuestion.existing_image_url ? <div><div className="small text-muted mb-1">Existing Image</div><img src={editQuestion.existing_image_url} alt="Existing" style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 8, border: "1px solid #e2e8f0" }} /></div> : null}
                    {editQuestion.new_image_preview ? <div><div className="small text-muted mb-1">New Preview</div><img src={editQuestion.new_image_preview} alt="New preview" style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 8, border: "1px solid #e2e8f0" }} /></div> : null}
                  </div>
                </div>
                <div className="col-md-6"><label className="form-label">Option A</label><input className="form-control" value={editQuestion.option_a || ""} onChange={(e) => setEditQuestion((p) => ({ ...p, option_a: e.target.value }))} /></div>
                <div className="col-md-6"><label className="form-label">Option B</label><input className="form-control" value={editQuestion.option_b || ""} onChange={(e) => setEditQuestion((p) => ({ ...p, option_b: e.target.value }))} /></div>
                <div className="col-md-6"><label className="form-label">Option C</label><input className="form-control" value={editQuestion.option_c || ""} onChange={(e) => setEditQuestion((p) => ({ ...p, option_c: e.target.value }))} /></div>
                <div className="col-md-6"><label className="form-label">Option D</label><input className="form-control" value={editQuestion.option_d || ""} onChange={(e) => setEditQuestion((p) => ({ ...p, option_d: e.target.value }))} /></div>
                <div className="col-md-6"><label className="form-label">Marks</label><input className="form-control" type="number" min="0" step="0.5" value={editQuestion.marks ?? 1} onChange={(e) => setEditQuestion((p) => ({ ...p, marks: e.target.value }))} /></div>
                <div className="col-md-6">
                  <label className="form-label">Correct Answer</label>
                  {isTrueFalseType(editQuestion.question_type) ? (
                    <select className="form-select" value={(currentCorrectArray(editQuestion.correct_answers)[0] || "A")} onChange={(e) => setEditQuestion((p) => ({ ...p, correct_answers: e.target.value }))}>
                      <option value="A">True</option><option value="B">False</option>
                    </select>
                  ) : isMultipleType(editQuestion.question_type) ? (
                    <div className="d-flex gap-3 flex-wrap">
                      {["A", "B", "C", "D"].map((key) => (
                        <label key={key} className="form-check-label">
                          <input className="form-check-input me-1" type="checkbox" checked={currentCorrectArray(editQuestion.correct_answers || "A").includes(key)} onChange={() => toggleCorrectOption(key)} />
                          {key}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <select className="form-select" value={(currentCorrectArray(editQuestion.correct_answers)[0] || "A")} onChange={(e) => setEditQuestion((p) => ({ ...p, correct_answers: e.target.value }))}>
                      <option value="A">A</option><option value="B">B</option><option value="C">C</option><option value="D">D</option>
                    </select>
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 px-3 py-2 border-top bg-light">
              <button className="btn btn-secondary" onClick={() => setEditQuestion(null)}>Close</button>
              <button className="btn btn-success" onClick={saveQuestion} disabled={savingQuestion}>{savingQuestion ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
    </DashboardLayout>
  );
}

export default ViewQuestionModulePage;
